import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { AttachEmailOutlined, ArrowBack } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { requestPasswordRecovery } from '../../../api/authService';
import { EmailAppIcon, GmailIcon, OutlookIcon } from '../../../icons';

type CheckYourEmailProps = {
  email: string;
  onBack: () => void;
};

/** Seconds before Resend is allowed again after a recovery email is sent. */
export const RESEND_COOLDOWN_SECONDS = 30;

const mailShortcutSx = {
  height: 40,
  minHeight: 40,
  width: '100%',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '100px',
  color: 'primary.main',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: 14,
  letterSpacing: '0.1px',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
} as const;

const mailShortcutIconSx = { width: 18, height: 18, display: 'block' } as const;

const CheckYourEmail: React.FC<CheckYourEmailProps> = ({ email, onBack }) => {
  const [isResending, setIsResending] = useState(false);
  // Email was already sent before this screen mounts - gate Resend immediately.
  const [cooldownSeconds, setCooldownSeconds] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setCooldownSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [cooldownSeconds]);

  const handleResend = async () => {
    if (cooldownSeconds > 0 || isResending) {
      return;
    }
    setIsResending(true);
    try {
      const result = await requestPasswordRecovery(email);
      if (result.isSuccess) {
        toast.success(result.message || 'Password recovery email sent successfully');
        setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      } else {
        toast.error(result.message || 'Failed to send password recovery email');
      }
    } finally {
      setIsResending(false);
    }
  };

  const isResendDisabled = isResending || cooldownSeconds > 0;
  const resendLabel = cooldownSeconds > 0 ? `Resend (wait ${cooldownSeconds}s)` : 'Resend';

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: '#F4C93C',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AttachEmailOutlined sx={{ fontSize: 20, color: '#FCFEFF' }} />
        </Box>
        <Typography
          component="h1"
          variant="h5"
          textAlign="center"
          sx={{ lineHeight: '28px' }}
        >
          Check your email
        </Typography>
      </Box>

      <Typography
        textAlign="center"
        sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.17px' }}
      >
        We sent the link to recover your password to{' '}
        <Box component="span" sx={{ fontWeight: 500 }}>
          {email}
        </Box>
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            component="a"
            href="https://mail.google.com"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            startIcon={
              <Box component="img" src={GmailIcon} alt="" sx={mailShortcutIconSx} />
            }
            sx={mailShortcutSx}
          >
            Open Gmail in browser
          </Button>
          <Button
            component="a"
            href="https://outlook.live.com/mail/"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            startIcon={
              <Box component="img" src={OutlookIcon} alt="" sx={mailShortcutIconSx} />
            }
            sx={mailShortcutSx}
          >
            Open Outlook in browser
          </Button>
          <Button
            component="a"
            href="mailto:"
            variant="outlined"
            startIcon={
              <Box component="img" src={EmailAppIcon} alt="" sx={mailShortcutIconSx} />
            }
            sx={mailShortcutSx}
          >
            Open email app
          </Button>
        </Box>

        <Typography
          textAlign="center"
          sx={{ fontSize: 14, lineHeight: '18px' }}
        >
          Didn&apos;t receive the link?{' '}
          <Box
            component="button"
            type="button"
            onClick={() => void handleResend()}
            disabled={isResendDisabled}
            aria-label={resendLabel}
            sx={{
              // this is a button but styled like a link
              border: 0,
              background: 'none',
              padding: 0,
              color: isResendDisabled ? 'text.disabled' : 'primary.main',
              fontSize: 14,
              lineHeight: '16px',
              textDecoration: 'underline',
              cursor: isResendDisabled ? 'default' : 'pointer',
              verticalAlign: 'baseline',
            }}
          >
            {isResending ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              resendLabel
            )}
          </Box>
        </Typography>

        <Typography
          component="button"
          type="button"
          onClick={onBack}
          sx={{
            // button styled like link
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            border: 0,
            background: 'none',
            padding: 0,
            color: 'primary.main',
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: '0.17px',
            textDecoration: 'underline',
            cursor: 'pointer',
            alignSelf: 'center',
          }}
        >
          <ArrowBack sx={{ fontSize: 16 }} aria-hidden />
          Back to login
        </Typography>
      </Box>
    </>
  );
};

export default CheckYourEmail;
